'use client';
import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Search } from 'lucide-react';
import Link from 'next/link';

// Generate stable mock data for a segment of 10,000 blocks
function generateSegmentBlocks(segmentStart: number) {
  const blocks = [];
  // Realistic distribution: ~47.5% purchased
  for (let i = 0; i < 10000; i++) {
    const blockId = segmentStart + i;
    // Use a deterministic pattern based on block ID
    const isPurchased = (blockId * 7 + blockId * blockId) % 100 < 48;
    const categories = ['health', 'love', 'finances', 'family', 'career', 'peace'];
    const categoryIndex = blockId % 6;
    blocks.push({
      id: `block-${blockId}`,
      number: blockId,
      purchased: isPurchased,
      category: isPurchased ? categories[categoryIndex] : null,
      owner: isPurchased ? `Seeker #${blockId % 9999 + 1}` : null,
    });
  }
  return blocks;
}

const SEGMENT_SIZE = 10000;
const TOTAL_BLOCKS = 6000000;
const TOTAL_SEGMENTS = TOTAL_BLOCKS / SEGMENT_SIZE;

const categoryColors: Record<string, string> = {
  health: '#22c55e',
  love: '#ec4899',
  finances: '#c9a84c',
  family: '#60a5fa',
  career: '#a78bfa',
  peace: '#f0abfc',
};

interface TooltipState {
  block: { id: string; number: number; purchased: boolean; category: string | null; owner: string | null };
  x: number;
  y: number;
}

type TooltipStateType = {
  block: { id: string; number: number; purchased: boolean; category: string | null; owner: string | null };
  x: number;
  y: number;
} | null;

export default function BlockMapGrid() {
  const [segment, setSegment] = useState(1);
  const [zoom, setZoom] = useState(2); // 1=tiny, 2=small, 3=medium
  const [tooltip, setTooltip] = useState<TooltipStateType>(null);
  const [search, setSearch] = useState('');

  const segmentStart = (segment - 1) * SEGMENT_SIZE + 1;
  const blocks = useMemo(() => generateSegmentBlocks(segmentStart), [segmentStart]);

  const blockSizes = [
    { label: 'Micro', size: 4, cols: 'grid-cols-[repeat(200,1fr)]' },
    { label: 'Small', size: 6, cols: 'grid-cols-[repeat(100,1fr)]' },
    { label: 'Medium', size: 10, cols: 'grid-cols-[repeat(80,1fr)]' },
  ];
  const currentZoom = blockSizes[zoom - 1];

  const handleSearch = () => {
    const num = parseInt(search);
    if (!isNaN(num) && num >= 1 && num <= TOTAL_BLOCKS) {
      const targetSegment = Math.ceil(num / SEGMENT_SIZE);
      setSegment(targetSegment);
    }
  };

  return (
    <div className="cosmic-card rounded-2xl overflow-hidden">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-2 flex-1 min-w-48">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Jump to block #..."
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-3 py-2 rounded-lg bg-primary/15 text-primary text-sm font-medium hover:bg-primary/25 transition-colors"
          >
            Go
          </button>
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs text-muted-foreground mr-2">Zoom:</span>
          <button
            onClick={() => setZoom(Math.max(1, zoom - 1))}
            disabled={zoom === 1}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-40"
          >
            <ZoomOut size={16} className="text-muted-foreground" />
          </button>
          <button
            onClick={() => setZoom(Math.min(3, zoom + 1))}
            disabled={zoom === 3}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-40"
          >
            <ZoomIn size={16} className="text-muted-foreground" />
          </button>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">
            Segment {segment.toLocaleString()} of {TOTAL_SEGMENTS.toLocaleString()}
          </span>
          <span className="text-xs text-muted-foreground ml-2">
            (Blocks #{segmentStart.toLocaleString()}–#{(segmentStart + SEGMENT_SIZE - 1).toLocaleString()})
          </span>
        </div>
      </div>

      {/* Grid */}
      <div className="relative overflow-auto p-4" style={{ maxHeight: '600px' }}>
        <div
          className="relative"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${zoom === 1 ? 200 : zoom === 2 ? 100 : 80}, ${currentZoom.size}px)`,
            gap: '1px',
          }}
        >
          {blocks.map((block) => (
            <div
              key={block.id}
              className="rounded-[1px] cursor-pointer transition-transform duration-100 hover:scale-150 hover:z-10 relative"
              style={{
                width: `${currentZoom.size}px`,
                height: `${currentZoom.size}px`,
                backgroundColor: block.purchased ? '#78350f' : '#166534',
                border: block.purchased ? '1px solid #c9a84c' : '1px solid #22c55e',
                boxShadow: block.purchased ? '0 0 3px rgba(201,168,76,0.4)' : 'none',
              }}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip({ block, x: rect.left, y: rect.top });
              }}
              onMouseLeave={() => setTooltip(null)}
            />
          ))}
        </div>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="fixed z-50 pointer-events-none cosmic-card rounded-xl p-3 text-xs shadow-xl border border-primary/20 min-w-[160px]"
            style={{ left: tooltip.x + 10, top: tooltip.y - 80 }}
          >
            <p className="font-display font-semibold text-foreground mb-1">Block #{tooltip.block.number.toLocaleString()}</p>
            <p className={`font-medium mb-1 ${tooltip.block.purchased ? 'text-primary' : 'text-success'}`}>
              {tooltip.block.purchased ? '● Claimed' : '● Available — $5'}
            </p>
            {tooltip.block.purchased && tooltip.block.category && (
              <p className="text-muted-foreground capitalize">
                Category: <span style={{ color: categoryColors[tooltip.block.category] }}>{tooltip.block.category}</span>
              </p>
            )}
            {!tooltip.block.purchased && (
              <Link href="/" className="text-primary hover:underline mt-1 block">Claim this block →</Link>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between p-4 border-t border-border">
        <button
          onClick={() => setSegment(Math.max(1, segment - 1))}
          disabled={segment === 1}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} /> Previous Segment
        </button>

        <div className="flex items-center gap-1">
          {[1, 2, 3, '...', 598, 599, 600].map((p, i) => (
            <React.Fragment key={`page-${i}-${p}`}>
              {p === '...' ? (
                <span className="px-2 text-muted-foreground text-sm">…</span>
              ) : (
                <button
                  onClick={() => setSegment(p as number)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${
                    segment === p
                      ? 'bg-primary/20 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {p}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        <button
          onClick={() => setSegment(Math.min(TOTAL_SEGMENTS, segment + 1))}
          disabled={segment === TOTAL_SEGMENTS}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next Segment <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}